import React from 'react';
import { DATA_TYPE_OPTIONS, FILE_PROVIDER_DATA_TYPE_OPTIONS, LOCKED_PROVIDER_TEXT_OPTIONS } from '../constants';
import { DataConnectionType, DataTypeValue, SourceColumn } from '../types';
import { coerceDataTypeForProvider, getColumnKey, getSourceDataType, isFullyEditableProvider, isUrlOnlyEditableProvider } from '../utilities';

interface ColumnSelectionGridProps {
    providerType?: DataConnectionType;
    columns: SourceColumn[];
    selectedColumnKeys: string[];
    dataTypesByColumnKey: Record<string, DataTypeValue>;
    onToggleColumn: (columnKey: string, selected: boolean) => void;
    onDataTypeChange: (columnKey: string, dataType: DataTypeValue) => void;
}

const ColumnSelectionGrid: React.FC<ColumnSelectionGridProps> = ({
    providerType,
    columns,
    selectedColumnKeys,
    dataTypesByColumnKey,
    onToggleColumn,
    onDataTypeChange,
}) => {
    const selectedSet = new Set(selectedColumnKeys);
    const selectedColumnCount = columns.reduce((count, column) => count + (selectedSet.has(getColumnKey(column)) ? 1 : 0), 0);
    const allColumnsSelected = columns.length > 0 && selectedColumnCount === columns.length;
    const someColumnsSelected = selectedColumnCount > 0 && !allColumnsSelected;
    const selectAllRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someColumnsSelected;
        }
    }, [someColumnsSelected]);

    React.useEffect(() => {
        columns.forEach(column => {
            const columnKey = getColumnKey(column);
            const sourceDataType = getSourceDataType(column);
            const currentDataType = dataTypesByColumnKey[columnKey] || sourceDataType;
            const coercedDataType = coerceDataTypeForProvider(providerType, sourceDataType, currentDataType);
            if (currentDataType !== coercedDataType) {
                onDataTypeChange(columnKey, coercedDataType);
            }
        });
    }, [columns, dataTypesByColumnKey, onDataTypeChange, providerType]);

    return (
        <div className="data-sources-column-grid">
            <table>
                <thead>
                    <tr>
                        <th>
                            <input
                                ref={selectAllRef}
                                type="checkbox"
                                checked={allColumnsSelected}
                                aria-checked={someColumnsSelected ? 'mixed' : allColumnsSelected}
                                aria-label={InovxLoader.t('facility:dataSources.fields.include')}
                                onChange={event => {
                                    const selected = event.target.checked;
                                    columns.forEach(column => onToggleColumn(getColumnKey(column), selected));
                                }}
                            />
                        </th>
                        <th>{InovxLoader.t('facility:dataSources.fields.columnName')}</th>
                        <th>{InovxLoader.t('facility:dataSources.fields.dataType')}</th>
                    </tr>
                </thead>
                <tbody>
                    {columns.map(column => {
                        const columnKey = getColumnKey(column);
                        const selected = selectedSet.has(columnKey);
                        const sourceDataType = getSourceDataType(column);
                        const value = coerceDataTypeForProvider(providerType, sourceDataType, dataTypesByColumnKey[columnKey] || sourceDataType);
                        const options = isFullyEditableProvider(providerType)
                            ? FILE_PROVIDER_DATA_TYPE_OPTIONS.filter(option => option.value !== 'url' || sourceDataType === 'text')
                            : isUrlOnlyEditableProvider(providerType) && sourceDataType === 'text'
                                ? LOCKED_PROVIDER_TEXT_OPTIONS
                                : DATA_TYPE_OPTIONS.filter(option => option.value === sourceDataType);
                        const dataTypeEditable = selected && (isFullyEditableProvider(providerType) || (isUrlOnlyEditableProvider(providerType) && sourceDataType === 'text'));
                        return (
                            <tr key={columnKey}>
                                <td>
                                    <input type="checkbox" checked={selected} onChange={event => onToggleColumn(columnKey, event.target.checked)} />
                                </td>
                                <td title={column.Identifier}>{column.Name}</td>
                                <td>
                                    <select
                                        value={value}
                                        disabled={!dataTypeEditable}
                                        onChange={event => onDataTypeChange(columnKey, event.target.value as DataTypeValue)}
                                    >
                                        {options.map(option => (
                                            <option key={option.value} value={option.value}>{InovxLoader.t(option.labelKey)}</option>
                                        ))}
                                    </select>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ColumnSelectionGrid;
